import { ChangeDetectorRef, Component, effect, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { DateAdapter } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';

import { IErrorNotification, IUser } from '@azavista/planner-lib';
import {
    AzavistaSharedService, FieldComponentType, IField, IObjectEnumFieldWithValue,
    IObjectEnumInputFieldValue
} from '@azavista/components/shared';
import { SharedService } from './shared/shared.service';
import { NotificationsService } from './shared/notifications.service';
import { AocClassNamePrefix, NotificationMessageType, SwitchMode } from './shared/enums';
import { CameraInversionMode, IAppConfig, IAppThemeChangedData, IScannedCodeValue, PreferredCamera, ScannedCodeSource } from './shared/interfaces';
import { EnvironmentService } from './shared/environment.service';
import { SubjectsService } from './shared/subjects.service';
import { AclService } from './shared/acl.service';
import { TimeBufferService } from './shared/time-buffer.service';
import { MediaMatcher } from '@angular/cdk/layout';
import { LayoutService } from './shared/layout.service';
import { CameraQrScanService, IQrCodeScanningConfig } from './shared/components/camera-qr-scan';
import { LOGO } from './shared/const';
import { ConfigurationStorageService, DEFAULT_APP_CONFIG, DEFAULT_PRINTER_SETTINGS, DEFAULT_HANDHELD_SCANNER_SETTINGS, IHandheldScannerSettings, IPrinterSettings, IQrCodeScanningSettings } from './shared/configuration-storage.service';
import { AppThemeService } from './shared/app-theme.service';
import { OnsiteCheckInClientService } from './shared/components/onsite-checkin-client/onsite-checkin-client.service';
import { OnsiteCheckInClientAuthService } from './shared/components/onsite-checkin-client/onsite-checkin-client-auth.service';
import { OnsiteCheckInClientErrorService } from './shared/components/onsite-checkin-client/onsite-checkin-client-error.service';
import { environment } from '../environments/environment';
import { SwitchModePopupComponent } from './event/switch-mode-popup/switch-mode-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { ISwitchModePopupComponentData } from './event/switch-mode-popup/interfaces';
import { DialogHelperService } from './shared/dialog-helper.service';
import { PrintServerApi } from '@azavista/planner-lib/lib/onsite-checkin/print-server-api';
import { UserPreferenceService } from './shared/user-preference.service';

@Component({
    selector: 'aoc-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css', './app-sidenav.component.css', './shared/components/aoc.components.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
    @ViewChild('scanQrCodeVideoElement', { static: true }) scanQrCodeVideoElement: ElementRef<HTMLVideoElement>;

    clientSvc = inject(OnsiteCheckInClientService);
    clientErrorSvc = inject(OnsiteCheckInClientErrorService);
    authSvc = inject(OnsiteCheckInClientAuthService);
    private userPreferenceSvc = inject(UserPreferenceService);

    logo = LOGO;
    qrError: string | Error;
    showQRCodeActions = false;
    selectedCameraId = this.cfgStorageSvc.getAppConfig()?.preferredCamera ?? PreferredCamera.environment;
    fields: IFields;
    containerClasses$ = this.layoutService.mainContainerClasses$;
    showCamera = false;
    kioskCheckInMode$ = this.userPreferenceSvc.getPropertySignal('kioskCheckInMode') ;
    SwitchMode = SwitchMode;

    mobileQuery: MediaQueryList;

    isKioskCheckInModeVisible = this.getIsKioskCheckInModeVisible(location.href);
    private mobileQueryListener: () => void;

    private listeningForKeys = false;
    private handheldScannerEnabled = false;
    private appConfig: IAppConfig;


    constructor(
        private environmentService: EnvironmentService,
        private sharedSvc: SharedService,
        private notificationsSvc: NotificationsService,
        private translateSvc: TranslateService,
        private router: Router,
        private subjectsSvc: SubjectsService,
        private readonly cmpSharedSvc: AzavistaSharedService,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        private readonly dateAdapter: DateAdapter<any>,
        private readonly aclSvc: AclService,
        private readonly printApi: PrintServerApi,
        private readonly timeBufferSvc: TimeBufferService,
        private layoutService: LayoutService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly media: MediaMatcher,
        private readonly cameraQrScanSvc: CameraQrScanService,
        private readonly cfgStorageSvc: ConfigurationStorageService,
        private readonly appThemeSvc: AppThemeService,
        private readonly matDialog: MatDialog,
        private readonly dialogHelperSvc: DialogHelperService
    ) {
        this.translateSvc.setDefaultLang('en-US');
        this.mobileQuery = this.media.matchMedia('(max-width: 426px)');
        this.mobileQueryListener = () => {
            this.changeDetectorRef.detectChanges();
            this.layoutService.isMobile$.set(this.mobileQuery.matches);
        };
        this.mobileQuery.addListener(this.mobileQueryListener);

        effect(() => {
            this.processAuthenticationNotification(this.authSvc.isAuthenticated$());
        });

        effect(() => {
            this.processCurrentUser(this.authSvc.user$());
        });

        effect(() => {
            const errorNotification = this.clientErrorSvc.errorNotification$();
            if (errorNotification){
                this.processApiErrorNotification(errorNotification);
            }
        });
    }

    async ngOnInit(): Promise<void> {
        this.fields = this.createFields();
        this.appConfig = this.cfgStorageSvc.getAppConfig() || {} as IAppConfig;
        this.setAppConfigDefaults(this.appConfig);
        this.setHandheldScannerDefaults(this.cfgStorageSvc.getHandheldScannerSettings() || {} as IHandheldScannerSettings);
        this.setQrCodeScannerDefaults(this.cfgStorageSvc.getQrCodeScanningSettings() || {} as IQrCodeScanningSettings);
        this.setPrinterDefaults(this.cfgStorageSvc.getPrinterSettings());
        this.setTimeBufferServiceOptions();
        this.loadThemeCSS();
        // TODO: For now we will set the configuration back to the storage so other parts of the app can read its defaults (if set)
        this.cfgStorageSvc.setAppConfig(this.appConfig);
        if (this.environmentService.isElectron()) {
            if (this.appConfig.startPrintServerExecutableOnStartUp) {
                this.environmentService.runExecutable(
                    this.appConfig.printServerExecutable,
                    `--address ${this.appConfig.printServerBaseUrl}`,
                    (outTxt?: string, errText?: string, errMsg?: string) => {
                        console.warn('Running executable output:', outTxt, errText, errMsg);
                    }
                );
            }
        }

        this.timeBufferSvc.getCandidatesObservable().subscribe(value => {
            this.processQRCode({ ...value, source: ScannedCodeSource.handheldScanner });
        });

        this.router.events.subscribe({
            next: event => this.processRouterEvent(event as RouterEvent)
        });
        this.subjectsSvc.getChangeHandheldScannerEnabledObservable().subscribe(value => this.handheldScannerEnabled = value);
        document.addEventListener('keydown', ev => this.keydownListener(ev));

        this.clientSvc.awaitInitCompleted().then(() => {
            if (this.authSvc.isAuthenticated$()) {
                this.navigateAfterAuthenticated();
            } else {
                // Not authenticated
                this.authSvc.navigateToLogin(window.location.href);
            }
        });

        this.cameraQrScanSvc.getScanResultObservable()
            .subscribe(scanResult => {
                const scanValue: IScannedCodeValue = {
                    value: scanResult.data,
                    source: ScannedCodeSource.camera,
                    duration: 0
                };
                this.processQRCode(scanValue);
            });

        this.subjectsSvc.getRequestQRCodeScanObservable()
            .subscribe(() => this.processRequestQRCodeScan());
        this.subjectsSvc.getStopQRCodeScanObservable()
            .subscribe(() => this.processStopQRCodeScan());

        this.subjectsSvc.getThemeChangedObservable().subscribe(
            appThemeChangedData => this.processAppThemeChanged(appThemeChangedData)
        );
    }

    private loadThemeCSS(): void {
        // TODO: We don't have themes the same way we have them in the planner app so the theme here must point to absolute path to a .css file
        const url = new URL(window.location.toString());
        const theme = url.searchParams.get('theme');
        if (theme) {
            // this.subjectsSvc.getThemeChangedSubject().next({ themeCssUrl: environment.resourcesUrlPrefix + `theme/${themeId}.css` });
            this.subjectsSvc.getThemeChangedSubject().next({ themeCssUrl: theme });
        } else {
            this.appThemeSvc.addAppThemeStylesheet('assets/themes/default.css');
        }
    }

    private async processAppThemeChanged(appThemeChangedData: IAppThemeChangedData): Promise<void> {
        try {
            await this.appThemeSvc.addAppThemeStylesheet(appThemeChangedData.themeCssUrl);
        } catch { }
    }

    onToolbarMenuButtonClick(): void {
        this.subjectsSvc.getToolbarMenuButtonClickedSubject().next();
    }

    private processRouterEvent(event: RouterEvent): void {
        if (event instanceof NavigationEnd) {
            const url = location.href;
            this.isKioskCheckInModeVisible = this.getIsKioskCheckInModeVisible(url);
        }
    }

    private getIsKioskCheckInModeVisible(url: string) {
        return url.includes('/participants') || url.includes('/kiosk-mode') || url.includes('/settings');
    }

    async processAuthenticationNotification(isAuthenticated: boolean): Promise<void> {
        if (isAuthenticated) {
            const tokenPayload = this.authSvc.tokenPayload$();
            this.aclSvc.setTokenPayload(tokenPayload);
            if (!this.listeningForKeys) {
                // TODO: We will change this - listening for keys to detect handheld scanned QR codes
                //       will happen only if the user says so - that way we will avoid unnecessary
                //       calls to the server if the user types something fast
                this.startListeningForKeys();
                this.listeningForKeys = true;
            }
            const user = this.authSvc.user$();
            this.processCurrentUser(user);
            const config  = this.cfgStorageSvc.getAppConfig() as IAppConfig;
            await this.clientSvc.init({
                token: tokenPayload,
                baseUrl: config.apiServerBaseUrl,
                clientId: environment.clientId,
                printServerBaseUrl: config.printServerBaseUrl,
            });

            this.navigateAfterAuthenticated();
        }
    }

    processQRCode(value: IScannedCodeValue): void {
        this.subjectsSvc.getQRCodeAvailableSubject().next(value);
    }

    processApiErrorNotification(err: IErrorNotification): void {
        if (this.clientErrorSvc.isApiAuthenticationError(err)) {
            // Navigate to login and put returnUrl in the URL
            this.authSvc.navigateToLogin(window.location.href);
            return;
        }
        try {
            const msg = this.clientErrorSvc.getErrorText(err);
            this.notificationsSvc.showNotification(NotificationMessageType.error, msg);
        } catch (caughtErr) {
            this.notificationsSvc.showNotification(NotificationMessageType.error, caughtErr.toString());
        }
    }

    processCurrentUser(user: IUser): void {
        if (!user) {
            return;
        }
        this.setLanguage(user.language);
        this.subjectsSvc.getCurrentUserAvailableSubject().next(user);
    }

    setLanguage(language: string): void {
        this.translateSvc.use(language);
        this.cmpSharedSvc.setLocale(language);
        this.dateAdapter.setLocale(language);
    }

    async onToggleCamera(): Promise<void> {
        if (!this.cameraQrScanSvc.isInitialized()) {
            return;
        }
        // TODO: Calling this again before the previous toggle ends can cause errors in the console
        //       "Uncaught (in promise): AbortError: The play() request was interrupted by a new load request. https://goo.gl/LdLk22"
        const preferredCamera = await this.cameraQrScanSvc.toggleCamera();
        this.cfgStorageSvc.setAppConfig({ preferredCamera: preferredCamera });
        this.selectedCameraId = preferredCamera;
    }

    onCloseCamera(): void {
        this.subjectsSvc.getStopQRCodeScanSubject().next();
    }

    private processRequestQRCodeScan(): void {
        // Show the shared video element
        this.showCamera = true;

        this.startQrScanning();
    }

    async startQrScanning(): Promise<void> {
        await this.setCameraQrScanInversionMode();
        return this.cameraQrScanSvc.start(this.getCameraScanConfig());
    }

    async setCameraQrScanInversionMode(): Promise<void> {
        const qrCodeScanningSettings = this.cfgStorageSvc.getQrCodeScanningSettings();
        await this.cameraQrScanSvc.setInversionMode(qrCodeScanningSettings.cameraInversionMode);
    }

    private getCameraScanConfig(): IQrCodeScanningConfig {
        // TODO: One of the settings is in the handheld scanner settings
        // We need this to be moved to a "global" app QR code scanning settings
        const handheldCfg = this.cfgStorageSvc.getHandheldScannerSettings();
        const qrScannerCfg = this.cfgStorageSvc.getQrCodeScanningSettings();
        return {
            videoElement: this.scanQrCodeVideoElement.nativeElement,
            ignoreSameCodesForMs: handheldCfg.ignoreSameCodesForMs,
            preferredCamera: this.selectedCameraId,
            cameraInversionMode: qrScannerCfg.cameraInversionMode,
        };
    }

    private setTimeBufferServiceOptions(): void {
        const timeBufferSvcOptions = this.timeBufferSvc.getOptions();
        const handheldScannerSettings = this.cfgStorageSvc.getHandheldScannerSettings();
        timeBufferSvcOptions.itemsMinLength = handheldScannerSettings.minimumCodeLength;
        timeBufferSvcOptions.maxMsBetweenCharacters = handheldScannerSettings.maxMsBetweenCharacters;
        this.timeBufferSvc.setOptions(timeBufferSvcOptions);
    }

    private setAppConfigDefaults(appConfig: IAppConfig): void {
        appConfig.apiServerBaseUrl ||= '';
        appConfig.printServerBaseUrl ||= DEFAULT_APP_CONFIG.printServerBaseUrl;
        appConfig.printServerExecutable ||= DEFAULT_APP_CONFIG.printServerExecutable;
        if (appConfig.startPrintServerExecutableOnStartUp === undefined) {
            appConfig.startPrintServerExecutableOnStartUp = DEFAULT_APP_CONFIG.startPrintServerExecutableOnStartUp;
        }
        if (appConfig.checkPrinterBeforePrinting === undefined) {
            appConfig.checkPrinterBeforePrinting = DEFAULT_APP_CONFIG.checkPrinterBeforePrinting;
        }
    }

    private setPrinterDefaults(settings: IPrinterSettings): void {
        settings.dpi ||= DEFAULT_PRINTER_SETTINGS.dpi;
        settings.cutter ??= DEFAULT_PRINTER_SETTINGS.cutter;
        this.cfgStorageSvc.setPrinterSettings(settings);
    }

    private setQrCodeScannerDefaults(settings: IQrCodeScanningSettings): void {
        settings.cameraInversionMode ||= CameraInversionMode.doNotUse;
        this.cfgStorageSvc.setQrCodeScanningSettings(settings);
    }

    private setHandheldScannerDefaults(settings: IHandheldScannerSettings): void {
        settings.ignoreSameCodesForMs ||= DEFAULT_HANDHELD_SCANNER_SETTINGS.ignoreSameCodesForMs;
        settings.maxMsBetweenCharacters ||= DEFAULT_HANDHELD_SCANNER_SETTINGS.maxMsBetweenCharacters;
        settings.minimumCodeLength ||= DEFAULT_HANDHELD_SCANNER_SETTINGS.minimumCodeLength;
        this.cfgStorageSvc.setHandheldScannerSettings(settings);
    }

    private processStopQRCodeScan(): void {
        this.showCamera = false;
        this.cameraQrScanSvc.stop();
    }

    private navigateAfterAuthenticated(): void {
        // When in Electron app, the window.location.pathname looks like "..../dist/" and does not contain the route
        // So if the user is authenticated and is electron, we will also navigate to events
        // Otherwise the Electron app still shows the login page even if there is token in the local storage
        const currentUrl = this.router.url;
        // Don't redirect if already on events page or event detail page
        if (currentUrl.startsWith('/events')) {
            return;
        }

        if (this.environmentService.isElectron()
            || window.location.pathname.endsWith('/login')
            // In case when we are at the base URL
            || window.location.pathname === document.head.querySelector('base')?.getAttribute('href')) {
            this.router.navigate(['/events']);
        }
    }

    private startListeningForKeys(): void {
        this.handheldScannerEnabled = this.cfgStorageSvc.getHandheldScannerSettings()?.enabled;
    }

    private stopListeningForKeys(): void {
        this.handheldScannerEnabled = false;
    }

    private keydownListener(ev: KeyboardEvent): void {
        if (!this.handheldScannerEnabled) {
            return;
        }
        const key = ev.key === 'Enter' ? '\n' : ev.key;
        // Keys with length more than one character
        // are most likely not something that comes from the handheld scanner
        // but typed by the user (arrows keys, shift/ctrl/tab etc.).
        // We will not process such keys
        const isProbablyManuallyTyped = key?.length > 1;
        if (!isProbablyManuallyTyped) {
            this.timeBufferSvc.addValue(key);
        }
    }

    private createFields(): IFields {
        const fields: IFields = {
            detectedCameras: {
                field: {
                    editable: true,
                    schema: { type: 'object' }, component: FieldComponentType.objectEnum
                } as IField,
                value: null as unknown as IObjectEnumInputFieldValue
            }
        };
        return fields;
    }

    onSwitchMode(newMode: SwitchMode): void {
        const componentData: ISwitchModePopupComponentData = {
            mode: newMode
        };

        this.matDialog.open<
            SwitchModePopupComponent,
            ISwitchModePopupComponentData,
            boolean
        >(
            SwitchModePopupComponent,
            {
                ...this.dialogHelperSvc.getDialogConfig(componentData, AocClassNamePrefix.aocAuto),
                width: '400px',
            }
        ).afterClosed().subscribe(dlgResult => {
            if (dlgResult) {
                this.userPreferenceSvc.patchParentSettings({
                    kioskCheckInMode: newMode
                });
            }
        });
    }
}

export interface IFields {
    detectedCameras: IObjectEnumFieldWithValue;
}
